import { createContext } from "react";
import { ApiService } from "../api";
import { ServiceInterface } from "../service.interface";
import {
  FSAdapterInterface,
  FSDownloadOptions,
  FSStatResult,
} from "./fs-adapter.interface";

interface FileDownload {}

export interface DownloadAndZipProgress {
  progress: number;
  bytesWritten: number;
  contentLength: number;
  extractPath?: string;
}

export const FileServiceContext = createContext<FileService>(null as any);

/**
 * File service is responsible for managing files in the ephermeral data
 * directory of the app. Downloaded resources and bundles are saved into the
 * caches directory, which means that the OS can delete these files at will
 * if it needs to. The OS never does this during runtime
 */
export class FileService implements ServiceInterface {
  public context = FileServiceContext;

  public readonly resourcePath = "resources";

  public get absoluteResourcesPath() {
    return this.resolvePath(this.resourcePath);
  }

  public resolveResourcesPath(path: string) {
    return `${this.resourcePath}/${path}`;
  }

  public resolveRelativeBundlePath(gameId: string, releaseVersion: number) {
    return `bundles/${gameId}/release_${releaseVersion}`;
  }

  public activeDownloads: FileDownload[] = [];
  public readonly baseDirectory = this.fsAdapter.CachesDirectoryPath;

  constructor(
    private apiService: ApiService,
    private fsAdapter: FSAdapterInterface
  ) {
    // Recheck active downloads
  }

  public async serviceWillMount() {
    // Void
  }

  public resolvePath(path: string) {
    return `${this.baseDirectory}/${path}`;
  }
  // Check if file exists
  public async exists(filePath: string): Promise<boolean> {
    return this.fsAdapter.exists(this.resolvePath(filePath));
  }

  // Check if file exists
  public async stat(filePath: string): Promise<FSStatResult> {
    return this.fsAdapter.stat(this.resolvePath(filePath));
  }

  public async mkdir(filePath: string): Promise<void> {
    return this.fsAdapter.mkdir(this.resolvePath(filePath));
  }

  public getDirectory(filePath: string) {
    const splitted = filePath.split("/");

    if (splitted.length <= 1) return filePath;

    return splitted.slice(0, splitted.length - 1).join("/");
  }

  public downloadFile(
    url: string,
    filePath: string,
    options: Pick<FSDownloadOptions, "begin" | "progress"> = {}
  ) {
    const download = this.fsAdapter.downloadFile({
      fromUrl: url,
      toFile: this.resolvePath(filePath),
      progressInterval: 250,
      connectionTimeout: 5000,
      readTimeout: 5000,
      begin: (_beg) => {
        // Noop
      },
      ...options,
    });

    return download;
  }

  public async upload(uri: string) {
    //const uri = filepath.replace("file:///", "file://");
    return this.apiService.clientPrivate.uploadClientFile({
      uri,
      name: "image.jpg",
      type: "image/jpeg",
    });
  }

  public async readFile(file: string) {
    return this.fsAdapter.readFile(this.resolvePath(file));
  }

  public async unlink(file: string) {
    return this.fsAdapter.unlink(this.resolvePath(file));
  }
}
